import { MRT_ColumnDef, MRT_VisibilityState } from "mantine-react-table"
import { ProductionTitleText } from "../../../-components/production-title"
import { StatusBadge } from "../../../-components/status-badge"
import { MRT_DateInputFilter } from "../../../-components/infinit-scrolling-table/mrt_date-input-filter"
import { DateOnly } from "../../../../../@client/System"
import {
  ActivityDto,
  ActivityFieldDto,
  ActivityTypeDto,
  StatusDto,
  TimePeriod,
} from "../../../../../@client/Theasoft_ApiServer_Activities"
import { ProductionTitle } from "../../../../../@client/Theasoft_ApiServer_Productions"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { ActivityTypeText } from "./activity-type-text"
import { useTranslation } from "react-i18next"
import { useTimePeriod } from "../../../../../utils/use-time-period"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { FieldData } from "../../../../../-components/field-data"

// TODO: components
export function useActivityTableColumns() {
  const { t } = useTranslation("app_server")

  const { getTimePeriod } = useTimePeriod()

  const { data: activityFieldTypes } = useSuspenseQueryGet(
    "/api/ProductionFieldType/GetActivityFieldTypes",
    null
  )

  const baseColumns = React.useMemo<MRT_ColumnDef<ActivityDto>[]>(
    () => [
      {
        accessorKey: "date",
        header: t("Date"),
        Cell: ({ cell }) =>
          getFormattedDate(cell.getValue<DateOnly>(), "ddd L"),
        Filter: ({ column }) => <MRT_DateInputFilter column={column} />,
      },
      {
        accessorKey: "timePeriod",
        header: t("TimePeriod"),
        Cell: ({ cell }) => getTimePeriod(cell.getValue<TimePeriod>()),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "activityType",
        header: t("ActivityType"),
        Cell: ({ cell, row }) => {
          const activityType = cell.getValue<ActivityTypeDto>()
          return (
            <ActivityTypeText
              name={activityType.name}
              rehearseNumber={row.original.rehearseNumber}
              foreColor={activityType.foreColor}
              backColor={activityType.backColor}
            />
          )
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "title",
        header: t("Title"),
        Cell: ({ cell }) => (
          <ProductionTitleText
            productionTitle={cell.getValue<ProductionTitle>()}
          />
        ),
        enableSorting: false,
      },
      { accessorKey: "location", header: t("Location") },
      { accessorKey: "artisticNote", header: t("ArtisticNote") },
      { accessorKey: "technicalNote", header: t("TechnicalNote") },
      {
        accessorKey: "status",
        header: t("Status"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <StatusBadge status={cell.getValue<StatusDto>()} />,
      },
    ],
    []
  )

  const extendedColumns = React.useMemo<MRT_ColumnDef<ActivityDto>[]>(
    () => [
      { accessorKey: "id", header: "Id" },
      ...baseColumns,
      ...activityFieldTypes.map<MRT_ColumnDef<ActivityDto>>((v) => ({
        accessorFn: (row) =>
          row.activityFields?.find(
            (field) => field.productionFieldTypeId === v.productionFieldTypeId
          ),
        header: v.name,
        Cell: ({ cell }) => {
          const cellValue = cell.getValue<ActivityFieldDto | undefined>()
          return cellValue ? (
            <FieldData
              fieldData={{
                textValue: cellValue.value,
                dataType: cellValue.fieldDataType,
              }}
            />
          ) : null
        },
        id: `activityField-${v.productionFieldTypeId}`,
        enableColumnFilter: false,
        enableSorting: false,
      })),
    ],
    []
  )

  const defaultColumnVisibility = React.useMemo<MRT_VisibilityState>(
    () => ({
      id: false,
      ...activityFieldTypes.reduce(
        (acc, v) => {
          acc[`activityField-${v.productionFieldTypeId}`] = false
          return acc
        },
        {} as Record<string, boolean>
      ),
    }),
    [activityFieldTypes]
  )

  return { baseColumns, extendedColumns, defaultColumnVisibility }
}
